import { useState, useEffect, useRef } from 'react';
import _ from "lodash";
import { 
  Stack,
  HStack,
  FormControl,
  Select,
  Checkbox,
  Radio, 
  RadioGroup,
  Switch,
  Button,
  Divider
} from '@chakra-ui/react'
import Autosuggest from 'react-autosuggest';
import clsx from 'clsx';
import { 
  basicCityOptions, 
  renderPeopleAmountOptions, 
  renderPriceOptions, 
  difficultyOptionsList, 
  featuresOptionsList 
} from 'utils';
import { getGamesTitleSuggestion } from 'src/api';
import { useUser, useGlobal } from 'src/context';
import styles from './GameSearchBar.module.css';

const labelText = {
  city: "遊玩城市",
  ft: "偏好特色",
  diff: "難度",
  feat: "特色或氣氛",
  pp: "遊玩人數",
  price: "每人預算",
  outdoor: "包含實境解謎",
  over: "包含已謝幕",
};

const GameSearchBar = (props) => {
  const { query, onSearch, onConditionChange, isHidingReviewed, onHideToggle } = props;
  
  const [keyword, setKeyword] = useState("");
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedPeople, setSelectedPeople] = useState("");
  const [selectedDiff, setSelectedDiff] = useState([]);
  const [selectedFeat, setSelectedFeat] = useState([]);
  const [selectedPrice, setSelectedPrice] = useState("");
  const [isOutdoorSelected, setIsOutdoorSelected] = useState(true);
  const [isOverSelected, setIsOverSelected] = useState(false);
  const [selectedOrderBy, setSelectedOrderBy] = useState("count");
  const [isBasicFieldsEmpty, setIsBasicFieldsEmpty] = useState(false); // 搜尋器的提示訊息

  const { isLogin } = useUser();

  useEffect(() => {
    setKeyword(query.q);
    if (query.city) setSelectedCities(query.city.split(","));
    setSelectedPeople(query.pp)
    const arr = query.ft.split(",");
    let diff = [], feat = [];
    arr.filter(ele => ele !== '').forEach(ele => {
      if (parseInt(ele) <= 3) {
        diff.push(ele);
      } else {
        feat.push(ele)
      }
    })

    setSelectedDiff(diff);
    setSelectedFeat(feat);
    setSelectedPrice(query.price);
    setIsOutdoorSelected(query.outdoor);
    setIsOverSelected(query.over);
    setSelectedOrderBy(query.orderby)
  }, [query]);

  useEffect(() => {
    if (isBasicFieldsEmpty) setIsBasicFieldsEmpty(false);
  }, [keyword, selectedCities, selectedPeople, selectedDiff, selectedFeat, selectedPrice]);

  const handleSearch = () => {
    if (keyword === '' 
      && selectedCities.length === 0 
      && selectedPeople === '' 
      && selectedDiff.length === 0 
      && selectedFeat.length === 0 
      && selectedPrice === '') {
      setIsBasicFieldsEmpty(true);
      return;
    }

    const ftArr = [...selectedDiff, ...selectedFeat];
    const ft = _.compact(ftArr);

    const newQuery = {
      q: keyword,
      city: selectedCities.length > 0 ? selectedCities.join() : "",
      pp: selectedPeople,
      ft: ft.join(),
      price: selectedPrice,
      outdoor: isOutdoorSelected,
      over: isOverSelected,
      orderby: selectedOrderBy,
      prmo: false,
      prnew: false,
    }

    onConditionChange(true);

    onSearch(newQuery);
  };

  const handleKeywordChange = (value) => {
    setKeyword(value);
  };

  const handleSubmitSearch = (suggestion = null) => {
    const newQuery = {
      q: suggestion || keyword,
      // city: selectedCities.length > 0 ? selectedCities.join() : "",
      // pp: selectedPeople,
      // ft: ft.join(),
      // price: selectedPrice,
      // outdoor: isOutdoorSelected,
      // over: isOverSelected,
      // orderby: selectedOrderBy,
      // prmo: false,
      // prnew: false,
    }

    onConditionChange(true);

    onSearch(newQuery);
  };

  const handleCitySelect = e => {
    const value = e.target.value;
    const newCities = selectedCities.indexOf(value) > -1 ? selectedCities.filter(v => v !== value) : [...selectedCities, value];
    setSelectedCities(newCities);
  };

  const handleDiffSelect = e => {
    const value = e.target.value;
    const newDiff = selectedDiff.indexOf(value) > -1 ? [] : [value];
    setSelectedDiff(newDiff);
  }

  const handleFeatSelect = e => {
    const value = e.target.value;
    const newFeats = selectedFeat.indexOf(value) > -1 ? selectedFeat.filter(v => v !== value) : [...selectedFeat, value];
    setSelectedFeat(newFeats)
  }

  const handleReset = () => {
    setKeyword("");
    setSelectedCities([]);
    setSelectedPeople("");
    setSelectedDiff([]);
    setSelectedFeat([]);
    setSelectedPrice("");
    setIsOutdoorSelected(true);
    setIsOverSelected(false);
    setSelectedOrderBy("count");
    onConditionChange(false);
    onHideToggle(false);
  }

  return (
    <div className='w-full mx-auto pb-3 px-0 lg:px-3 flex flex-col z-10 bg-white lg:shadow lg:rounded gap-y-2'>
      <TitleQuerySuggest 
        keyword={keyword}
        onInputChange={handleKeywordChange}
        onSearch={handleSubmitSearch}
      />
      <Divider />
      <MultiSelector
        type="city"
        options={basicCityOptions}
        values={selectedCities}
        onChange={handleCitySelect}
        maxCount={3}
      />
      <Divider />
      <OptionSelector
        type="pp"
        value={selectedPeople}
        onChange={e => setSelectedPeople(e.target.value)}
      />
      <MultiSelector
        type="diff"
        options={difficultyOptionsList}
        values={selectedDiff}
        onChange={handleDiffSelect}
        maxCount={1}
      />
      <Divider />
      <MultiSelector
        type="feat"
        options={featuresOptionsList}
        values={selectedFeat}
        onChange={handleFeatSelect}
        maxCount={2}
      />
      <Divider />
      <OptionSelector
        type="price"
        value={selectedPrice}
        onChange={e => setSelectedPrice(e.target.value)}
      />
      {isBasicFieldsEmpty && (
        <p className='text-xs text-red-500'>
          請在以上條件中設定至少一項
        </p>
      )}
      <p className='mb-1 text-sm font-medium'>其他條件</p>
      <Stack spacing={1}>
        <ToggleSelector
          type="outdoor"
          value={isOutdoorSelected}
          onChange={() => setIsOutdoorSelected(prev => !prev)}
        />
        <ToggleSelector
          type="over"
          value={isOverSelected}
          onChange={() => setIsOverSelected(prev => !prev)}
        />
      </Stack>
      <Divider />
      <OrderSelector 
        value={selectedOrderBy}
        onChange={setSelectedOrderBy}
      />
      <Divider />
      <FormControl>
        <HStack spacing={2}>
          <Switch 
            isChecked={isHidingReviewed} 
            onChange={onHideToggle}
            isDisabled={!isLogin}
            colorScheme='secondary'
            name="customization" 
            id='customization' 
          />
          <label htmlFor='customization'>
            只顯示尚未遊玩的
          </label>
        </HStack>
      </FormControl>
      <div className='sticky lg:relative top-0 py-3 -order-1 lg:order-none z-10 bg-white border-b-gray lg:border-b-none border-b-2 flex justify-evenly'>
        <Button
          variant="outline"
          onClick={handleReset}
        >
          重設
        </Button>
        <Button
          variant="solid"
          colorScheme="secondary"
          onClick={handleSearch}
        >
          搜尋
        </Button>
      </div>
    </div>
  )
};

// 多選多：城市、特色（難度與氣氛）
const MultiSelector = props => {
  const { type, options, values, onChange, maxCount } = props;

  return (
    <div>
      <p className='mb-1 text-sm font-medium'>{labelText[type]}</p>
      <div className='grid grid-cols-2 gap-1'>
        {options.map((ele, idx) => {
          return (
            <div key={`${type}-${ele.name}`}>
              <FormControl display='flex' alignItems='center'>
                <HStack spacing={2}>
                  <Checkbox 
                    isChecked={values.indexOf(ele.value) > -1} 
                    onChange={onChange}
                    isDisabled={values.length >= maxCount && values.indexOf(ele.value) === -1}
                    colorScheme='secondary'
                    name={ele.name}
                    id={ele.name}
                    value={ele.value}
                  />
                  <label htmlFor={ele.name}>
                    {ele.name}
                  </label>
                </HStack>
              </FormControl>
            </div>
          )
      })}
      </div>
    </div>
  )
};

// 下拉選單：人數、價格
const OptionSelector = props => {
  const { type, value, onChange } = props;

  const options = type === "pp" ? renderPeopleAmountOptions : renderPriceOptions;

  return (
    <FormControl>
      <p className='mb-1 text-sm font-medium'>{labelText[type]}</p>
      <Select 
        variant='flushed'
        value={value} 
        onChange={onChange}
      >
        {options()}
      </Select>
    </FormControl>
  );
}

// 布林切換：實境解謎、已謝幕
const ToggleSelector = (props) => {
  const { type, value, onChange } = props;

  return (
    <FormControl>
      <HStack spacing={2}>
        <Checkbox 
          isChecked={value} 
          onChange={onChange}
          colorScheme='secondary'
          name={type}
          id={type}
        />
        <label htmlFor={type}>
          {labelText[type]}
        </label>
      </HStack>
    </FormControl>
  );
};

//  多擇一：排序方式
const OrderSelector = props => {
  const { value, onChange } = props;

  return (
    <FormControl>
      <p className='mb-1 text-sm font-medium'>排序方式</p>
      <RadioGroup 
        colorScheme='secondary'
        value={value} 
        onChange={onChange}
      >
        <Stack spacing={1}>
          <Radio value="count">評價數量</Radio>
          <Radio value="score">評價分數</Radio>
          <Radio value="time">開幕日期</Radio>
        </Stack>
      </RadioGroup>
    </FormControl>
  );
}

const TitleQuerySuggest = props => {
  const { keyword, onInputChange, onSearch } = props;

  const { isScreenLg } = useGlobal();

  const [suggestions, setSuggestions] = useState([]);

  const suggestionTimer = useRef(null);

  const clearSuggestion = () => {
    setSuggestions([]);
  };

  const handleKeywordInput = (e, { newValue, method }) => {
    if (['enter', 'click'].includes(method)) {
      clearTimeout(suggestionTimer.current);
      clearSuggestion();
    }

    if (method === 'type') {
      onInputChange(newValue);
      
      if (suggestionTimer.current) {
        clearTimeout(suggestionTimer.current);
      }

      suggestionTimer.current = setTimeout(() => {
        if (newValue.length > 1) fetchSuggestion(newValue);
      }, 400);
    } else {
      
    }
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 13) {
      onSearch();

      if (suggestionTimer.current) {
        clearTimeout(suggestionTimer.current);
      }
    }
  }

  const fetchSuggestion = async (q) => {
    const res = await getGamesTitleSuggestion(q);

    if (res.data && res.data.length) {
      setSuggestions(res.data)
    }
  };


  const handleSelected = (e, { suggestion }) => {
    onInputChange(suggestion);

    onSearch(suggestion)
  }

  const fetchRequested = ({ value, reason }) => {
    switch (reason) {
      case 'input-changed':
        onInputChange(value)
        break;
      case 'input-focused':
        break;
      case 'escape-pressed':
        break;
      case 'suggestions-revealed':
        break;
      case 'suggestion-selected':
        break;
    }
  }

  const getSuggestionValue = (suggestion) => {
    return suggestion;
  };

  const shouldRenderSuggestions = (value, reason) => {
    return value.trim().length > 1 && ['input-changed', 'input-focused', 'render'].includes(reason);
  }

  const renderSuggestion = (suggestion, { query, isHighlighted }) => {
    return (
      <div className={clsx('p-2 cursor-pointer', { 'bg-primary text-white' : isHighlighted })}>
        {suggestion}
      </div>
    )
  };

  const inputProps = {
    className: 'w-full p-2 border border-gray-300 rounded-sm',
    placeholder: '輸入關鍵字 (選填)',
    value: keyword,
    onChange: handleKeywordInput,
    onKeyDown: handleKeyDown
  }

  const containerProps = {
    className: 'shadow rounded-sm'
  }

  return (
    <div>
      <p className='my-1 text-sm font-medium'>遊戲或工作室名稱</p>
      <Autosuggest
        suggestions={suggestions}
        onSuggestionSelected={handleSelected}
        onSuggestionsFetchRequested={fetchRequested}
        onSuggestionsClearRequested={clearSuggestion}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        shouldRenderSuggestions={shouldRenderSuggestions}
        inputProps={inputProps}
        containerProps={containerProps}
        theme={styles}
        focusInputOnSuggestionClick={isScreenLg}
        id='title-suggest'
      />
    </div>
  )
};

export default GameSearchBar;
